import { BodyText, Separator, toast } from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { Observation as FhirObservation } from "@curai/js-fhir-tools";
import { useParams, useSearch } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";
import { Fragment, useState } from "react";
import type { z } from "zod";
import { useCreateGenericObservation } from "../../network/useCreateGenericObservation";
import { useUpdateGenericObservation } from "../../network/useUpdateGenericObservation";
import { useVoidGenericObservation } from "../../network/useVoidGenericObservation";
import { ObservationEditor } from "./ObservationEditor";
import type { editObservationSchema } from "./schemas";

interface ObservationProps {
  observation: fhirTypes.Observation | null;
  observationCode?: fhirTypes.CodeableConcept;
  disabled?: boolean;
}

export const Observation = ({
  observation,
  observationCode,
  disabled,
}: ObservationProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  const [isEditing, setIsEditing] = useState(false);

  const createObservation = useCreateGenericObservation({
    patientId,
    encounterId: encounterId as string,
  });

  const updateObservation = useUpdateGenericObservation({
    patientId,
    encounterId: encounterId as string,
    observationId: observation?.id,
  });

  const voidObservation = useVoidGenericObservation({
    patientId,
    encounterId: encounterId as string,
  });

  const label = observation?.code.coding?.[0].display || observationCode?.text;
  const isLoading =
    createObservation.isPending ||
    updateObservation.isPending ||
    voidObservation.isPending;

  const onSubmit = async (values: z.infer<typeof editObservationSchema>) => {
    if (!observation) {
      // observation doesn't exist yet -- this usecase is for pre-defined EXAM coding concepts
      try {
        await createObservation.mutateAsync({
          patientId,
          encounterId: encounterId as string,
          category: FhirObservation.Category.EXAM,
          method: FhirObservation.Method.values.MANUAL_FREE_TEXT,
          code: {
            coding: [
              {
                code: values.code.coding[0].code,
                display: values.code.coding[0].display,
                system: values.code.coding[0].system,
              },
            ],
            text: values.code.text || values.code.coding[0].display,
          },
          status: FhirObservation.Status.FINAL,
          fhirValue: {
            valueString: values.observationValue,
          },
        });
        setIsEditing(false);
      } catch (error) {
        toast.error("Failed to create observation");
      }
    } else {
      try {
        await updateObservation.mutateAsync({
          fhirValue: {
            valueString: values.observationValue,
          },
        });
        setIsEditing(false);
      } catch (error) {
        toast.error("Failed to update observation");
      }
    }
  };

  if (isEditing) {
    return (
      <>
        <Separator />
        <div className="my-3">
          <ObservationEditor
            onCancel={() => setIsEditing(false)}
            isLoading={isLoading}
            onSubmit={onSubmit}
            observation={observation}
            observationCode={observationCode}
          />
        </div>
      </>
    );
  }

  return (
    <Fragment>
      <Separator />
      <EditableRecordButton
        onClick={() => setIsEditing(true)}
        onDelete={
          observation
            ? () =>
                voidObservation.mutate({
                  observationId: observation.id as string,
                })
            : undefined
        }
        deletePending={voidObservation.isPending}
        disabled={disabled}
      >
        <div className="flex flex-col gap-1 pt-1">
          <BodyText
            bold
            size="sm"
            textColor={disabled ? "tertiary" : "primary"}
          >
            {label}
          </BodyText>
          <BodyText
            size="sm"
            textColor={
              disabled || !observation?.valueString ? "tertiary" : "primary"
            }
          >
            {observation?.valueString || "None"}
          </BodyText>
        </div>
      </EditableRecordButton>
    </Fragment>
  );
};
